/**
 * src/data/TechStackBank.js
 * Hardcoded scenario bank for Module 3: Workplace Tech Stack
 */

export const techStackBank = [
  {
    "id": "techstack_01",
    "artifactDescription": "A client emails you an updated, signed Statement of Work (SOW) mid-project.",
    "sourceChannel": "Direct Email Inbox",
    "options": [
      {
        "route": "System of Record",
        "destination": "Upload to the Salesforce Account Record",
        "isCorrect": true,
        "feedback": "Correct. Uploading legal contracts to the CRM ensures auditability, single source of truth, and cross-functional visibility."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Attach to a Slack DM to the Project Manager",
        "isCorrect": false,
        "feedback": "Violation. Dropping contracts exclusively into ephemeral chat creates dark data and version control fragmentation."
      }
    ]
  },
  {
    "id": "techstack_02",
    "artifactDescription": "A user reports a critical checkout error during a live customer support call.",
    "sourceChannel": "Inbound Call",
    "options": [
      {
        "route": "System of Record",
        "destination": "Log an urgent ticket in Jira with detailed error steps",
        "isCorrect": true,
        "feedback": "Correct. Logging tickets in the official tracking system feeds the technical team's dashboard and tracks resolution times."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Ping the technical lead on Teams: 'Hey, checkout is broken'",
        "isCorrect": false,
        "feedback": "Violation. Informal pings without structured error steps create notification clutter and lack ticket tracking."
      }
    ]
  },
  {
    "id": "techstack_03",
    "artifactDescription": "You just finished a panel interview for a Senior Management Candidate and need to submit your hiring recommendation.",
    "sourceChannel": "Zoom Meeting",
    "options": [
      {
        "route": "System of Record",
        "destination": "Submit your evaluation scorecard in the official Applicant Tracking System (ATS)",
        "isCorrect": true,
        "feedback": "Correct. Centralizing interview feedback ensures compliance, mitigates bias, and maintains a unified candidate profile."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Slack the recruiter: 'I liked him, let's hire.'",
        "isCorrect": false,
        "feedback": "Violation. Circumventing the ATS leaves no auditable paper trail for HR compliance and lacks specific evaluation data."
      }
    ]
  },
  {
    "id": "techstack_04",
    "artifactDescription": "You need to share the login password for a shared corporate social media account with a new team member.",
    "sourceChannel": "Colleague Request",
    "options": [
      {
        "route": "System of Record",
        "destination": "Provision access via the official Corporate Password Manager (e.g., 1Password)",
        "isCorrect": true,
        "feedback": "Correct. Using an enterprise password manager ensures encryption, access logs, and the ability to revoke access upon offboarding."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Text the password to their personal phone",
        "isCorrect": false,
        "feedback": "Violation. Transmitting credentials over unencrypted, unmanaged channels is a severe InfoSec vulnerability."
      }
    ]
  },
  {
    "id": "techstack_05",
    "artifactDescription": "You created a complex spreadsheet that automates a massive chunk of data processing for the finance team.",
    "sourceChannel": "Local Desktop",
    "options": [
      {
        "route": "System of Record",
        "destination": "Upload and sync to the secure corporate shared drive (e.g., SharePoint / Google Drive)",
        "isCorrect": true,
        "feedback": "Correct. Cloud storage ensures version history, peer review, and business continuity if your machine crashes."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Save as 'finance_automation_FINAL.xlsx' on your desktop",
        "isCorrect": false,
        "feedback": "Violation. Local storage creates a massive key-person dependency and risks total data loss if your hardware fails."
      }
    ]
  },
  {
    "id": "techstack_06",
    "artifactDescription": "You take detailed strategic notes during a highly confidential meeting with external vendors.",
    "sourceChannel": "Meeting Audio",
    "options": [
      {
        "route": "System of Record",
        "destination": "Document in the team's secure knowledge base (e.g., Confluence / Notion)",
        "isCorrect": true,
        "feedback": "Correct. Storing documentation in the central wiki democratizes knowledge and makes strategic decisions searchable."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Type them into Apple Notes on your personal iPad",
        "isCorrect": false,
        "feedback": "Violation. Siloing corporate IP on a personal device creates data silos and violates device management policies."
      }
    ]
  },
  {
    "id": "techstack_07",
    "artifactDescription": "A customer accidentally pastes their full unencrypted credit card number into a support chat.",
    "sourceChannel": "Customer Support Chat",
    "options": [
      {
        "route": "System of Record",
        "destination": "Use the Zendesk automated redaction tool and flag to security",
        "isCorrect": true,
        "feedback": "Correct. Redacting sensitive data at the source maintains compliance and protects the company from massive legal fines."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Screenshot it and Slack it to Finance to process manually",
        "isCorrect": false,
        "feedback": "Violation. Propagating unencrypted credit card data into Slack creates a secondary, highly illegal exposure vector."
      }
    ]
  },
  {
    "id": "techstack_08",
    "artifactDescription": "You want to use generative AI to summarize and rewrite a highly confidential, proprietary corporate strategy document.",
    "sourceChannel": "Strategy Document",
    "options": [
      {
        "route": "System of Record",
        "destination": "Use the approved Enterprise Corporate AI tool",
        "isCorrect": true,
        "feedback": "Correct. Enterprise-tier AI tools guarantee zero data retention and protect company IP from public training models."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Paste the document into a free, public ChatGPT account",
        "isCorrect": false,
        "feedback": "Violation. Uploading proprietary data to public AI models constitutes a massive data breach and loss of intellectual property."
      }
    ]
  },
  {
    "id": "techstack_09",
    "artifactDescription": "You are negotiating terms of service with a new vendor and they send back contractual redlines.",
    "sourceChannel": "Email Attachment",
    "options": [
      {
        "route": "System of Record",
        "destination": "Upload to the Corporate Contract Management System for legal review",
        "isCorrect": true,
        "feedback": "Correct. Using a Contract Lifecycle Management tool tracks revision history and blocks unauthorized legal commitments."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Save to local downloads, edit the PDF, and email back",
        "isCorrect": false,
        "feedback": "Violation. Local redlining causes version drift and entirely bypasses mandatory legal risk assessments."
      }
    ]
  },
  {
    "id": "techstack_10",
    "artifactDescription": "A major enterprise client requests a new analytics dashboard feature during a quarterly business review.",
    "sourceChannel": "Zoom QBR",
    "options": [
      {
        "route": "System of Record",
        "destination": "Log the request in the Product Management portal (e.g., Jira)",
        "isCorrect": true,
        "feedback": "Correct. Routing feature requests to the product management tool enables roadmap prioritization and impact scoring."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "DM the Product Manager: 'Client really wants a new dashboard.'",
        "isCorrect": false,
        "feedback": "Violation. Passing feature requests via chat lacks context, cannot be quantified, and vanishes into the chat history."
      }
    ]
  },
  {
    "id": "techstack_11",
    "artifactDescription": "You need to map out a complex departmental restructuring plan.",
    "sourceChannel": "Management Whiteboard Session",
    "options": [
      {
        "route": "System of Record",
        "destination": "Build the diagram in the approved corporate Lucidchart account",
        "isCorrect": true,
        "feedback": "Correct. Enterprise diagramming tools ensure access controls, version history, and single sign-on security."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Create a free Miro account with your personal email",
        "isCorrect": false,
        "feedback": "Violation. Shadow IT. Using unapproved freemium software hosts sensitive corporate data on unsecured third-party servers."
      }
    ]
  },
  {
    "id": "techstack_12",
    "artifactDescription": "You return from a business trip and need to process $2,000 in flight and hotel receipts.",
    "sourceChannel": "Physical/Digital Receipts",
    "options": [
      {
        "route": "System of Record",
        "destination": "Upload directly to the official expense system (e.g., Concur / Expensify)",
        "isCorrect": true,
        "feedback": "Correct. Direct upload to the expense management system ensures audit compliance, fast routing, and tax documentation."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Put them in a personal Dropbox folder and share the link",
        "isCorrect": false,
        "feedback": "Violation. Forcing finance to access third-party unapproved storage creates friction and violates financial record policies."
      }
    ]
  },
  {
    "id": "techstack_13",
    "artifactDescription": "A key account tells you they are canceling their subscription due to poor product performance.",
    "sourceChannel": "Client Email",
    "options": [
      {
        "route": "System of Record",
        "destination": "Log the cancellation reason and update the health score in Salesforce",
        "isCorrect": true,
        "feedback": "Correct. Structured cancellation logging feeds the data warehouse, allowing leadership to analyze systemic revenue leaks."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Vent about the cancellation in the team's WhatsApp group",
        "isCorrect": false,
        "feedback": "Violation. Gossiping about lost revenue on a shadow communication channel hides the data from executives and prevents root-cause analysis."
      }
    ]
  },
  {
    "id": "techstack_14",
    "artifactDescription": "The design agency delivers the final high-resolution logo files for the company rebrand.",
    "sourceChannel": "WeTransfer Link",
    "options": [
      {
        "route": "System of Record",
        "destination": "Upload to the official Corporate Digital Asset Manager (DAM)",
        "isCorrect": true,
        "feedback": "Correct. A centralized DAM ensures all employees have access to the latest, approved brand assets, preventing brand fragmentation."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Leave them in a zip file in your local 'Downloads' folder",
        "isCorrect": false,
        "feedback": "Violation. Hoarding brand assets locally means other teams will inevitably use old, outdated logos they find on Google."
      }
    ]
  },
  {
    "id": "techstack_15",
    "artifactDescription": "Your corporate laptop screen starts aggressively flickering and rebooting randomly.",
    "sourceChannel": "Hardware Failure",
    "options": [
      {
        "route": "System of Record",
        "destination": "Submit a hardware replacement ticket via the IT Helpdesk portal",
        "isCorrect": true,
        "feedback": "Correct. IT asset management requires ticket trails to track hardware lifecycles, warranty claims, and depreciation."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Walk up to the IT desk and say 'My laptop is broken.'",
        "isCorrect": false,
        "feedback": "Violation. 'Shoulder tapping' bypasses queues, disrupts IT workflow, and leaves no record of the hardware failure."
      }
    ]
  },
  {
    "id": "techstack_16",
    "artifactDescription": "You scan 200 badges at a major trade show and now have a massive list of warm leads.",
    "sourceChannel": "Badge Scanner App",
    "options": [
      {
        "route": "System of Record",
        "destination": "Import directly into Salesforce / HubSpot via integration",
        "isCorrect": true,
        "feedback": "Correct. CRM integration triggers automated marketing campaigns and tracks event ROI immediately."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Export to a local CSV and email it to the sales team",
        "isCorrect": false,
        "feedback": "Violation. Local CSVs cause duplicate records, violate data privacy opt-in tracking, and prevent automated marketing follow-up."
      }
    ]
  },
  {
    "id": "techstack_17",
    "artifactDescription": "You are issued a new corporate credit card number for purchasing departmental software.",
    "sourceChannel": "Corporate Finance",
    "options": [
      {
        "route": "System of Record",
        "destination": "Store in the secure Corporate Password Vault",
        "isCorrect": true,
        "feedback": "Correct. Secure vault systems store credentials safely, meaning the plaintext information is protected and access is logged."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Paste it into the team Slack channel so everyone has it",
        "isCorrect": false,
        "feedback": "Violation. Pasting plaintext credit card numbers in Slack is a fireable security offense that exposes corporate funds to massive risk."
      }
    ]
  },
  {
    "id": "techstack_18",
    "artifactDescription": "You finish drafting the final Q3 financial revenue projections for the board meeting.",
    "sourceChannel": "Financial Modeling",
    "options": [
      {
        "route": "System of Record",
        "destination": "Save to the secure Corporate SharePoint / Google Drive with restricted permissions",
        "isCorrect": true,
        "feedback": "Correct. Centralized cloud storage with strict access controls ensures sensitive financial data is version-controlled and secure."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Email the Excel file to the executives as an attachment",
        "isCorrect": false,
        "feedback": "Violation. Emailing attachments creates irreversible version drift and poses a high risk of accidental forwarding to unauthorized parties."
      }
    ]
  },
  {
    "id": "techstack_19",
    "artifactDescription": "You finalize the 12-month strategic roadmap based on executive feedback.",
    "sourceChannel": "Strategy Planning",
    "options": [
      {
        "route": "System of Record",
        "destination": "Publish in the centralized strategic planning portal (e.g., Jira / Asana)",
        "isCorrect": true,
        "feedback": "Correct. Dedicated roadmap tools link high-level strategy directly to execution and automatically update for all stakeholders."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Make a colorful PowerPoint and save it to your desktop",
        "isCorrect": false,
        "feedback": "Violation. A static desktop presentation becomes instantly outdated the next day, leading to devastating cross-functional misalignment."
      }
    ]
  },
  {
    "id": "techstack_20",
    "artifactDescription": "Legal issues a mandatory preservation hold on all documents relating to a specific vendor dispute.",
    "sourceChannel": "Legal Directive",
    "options": [
      {
        "route": "System of Record",
        "destination": "Flag the communications in the corporate E-Discovery / Archiving platform",
        "isCorrect": true,
        "feedback": "Correct. E-Discovery tools freeze data immutably, ensuring strict legal compliance and admissibility in court."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Forward all related emails to a personal Gmail backup",
        "isCorrect": false,
        "feedback": "Violation. Exporting data to a personal account during a legal hold is considered destruction of evidence and carries criminal risk."
      }
    ]
  },
  {
    "id": "techstack_21",
    "artifactDescription": "You are writing the documentation for a new company-wide Standard Operating Procedure (SOP) that all teams must follow.",
    "sourceChannel": "Operations",
    "options": [
      {
        "route": "System of Record",
        "destination": "Publish via the official internal company wiki (e.g., Confluence)",
        "isCorrect": true,
        "feedback": "Correct. Centralized knowledge bases provide dynamic, searchable documentation that stays synchronized across the company."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Write it up in a personal Notion workspace",
        "isCorrect": false,
        "feedback": "Violation. Siloed SOPs are undiscoverable, quickly decay into inaccuracy, and block cross-functional alignment."
      }
    ]
  },
  {
    "id": "techstack_22",
    "artifactDescription": "You receive updated French translation documents for the new marketing campaign.",
    "sourceChannel": "Translation Agency",
    "options": [
      {
        "route": "System of Record",
        "destination": "Upload directly to the corporate Translation Management System",
        "isCorrect": true,
        "feedback": "Correct. Translation management systems organize localized assets and automatically deploy approved language updates."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Drag and drop the files into a Slack channel",
        "isCorrect": false,
        "feedback": "Violation. Passing documents via chat guarantees version conflicts and manually breaks the approval workflow."
      }
    ]
  },
  {
    "id": "techstack_23",
    "artifactDescription": "An employee resigns, and you need to process their departure steps.",
    "sourceChannel": "HR Workflow",
    "options": [
      {
        "route": "System of Record",
        "destination": "Trigger the Offboarding workflow in the HR portal (e.g., Workday)",
        "isCorrect": true,
        "feedback": "Correct. HR system workflows automatically trigger IT access revocation, payroll finalization, and compliance checklists."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Use a text file on your desktop to track what to do",
        "isCorrect": false,
        "feedback": "Violation. Manual offboarding guarantees steps will be missed, resulting in former employees retaining access to company systems."
      }
    ]
  },
  {
    "id": "techstack_24",
    "artifactDescription": "You need to analyze 5 million rows of user behavioral data to find usage trends.",
    "sourceChannel": "Data Request",
    "options": [
      {
        "route": "System of Record",
        "destination": "Query it via secure Business Intelligence tools (e.g., Tableau / Snowflake)",
        "isCorrect": true,
        "feedback": "Correct. Business Intelligence tools process massive datasets securely in the cloud, maintaining a single source of truth."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Download the data as a massive local CSV file",
        "isCorrect": false,
        "feedback": "Violation. Downloading millions of rows of user data to a local machine creates a massive security vulnerability and will likely crash your computer."
      }
    ]
  },
  {
    "id": "techstack_25",
    "artifactDescription": "The marketing team updates the official brand color palette and font guidelines.",
    "sourceChannel": "Brand Refresh",
    "options": [
      {
        "route": "System of Record",
        "destination": "Update the Figma Design System and publish changes",
        "isCorrect": true,
        "feedback": "Correct. Updating the design system library automatically pushes the new styles to all active workspaces across the company."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Email a PDF of the new colors to the company",
        "isCorrect": false,
        "feedback": "Violation. Emailed PDFs are instantly buried. Teams will continue using the old colors because the centralized system wasn't updated."
      }
    ]
  },
  {
    "id": "techstack_26",
    "artifactDescription": "You move to a new apartment and need to update your home address for tax purposes.",
    "sourceChannel": "Life Event",
    "options": [
      {
        "route": "System of Record",
        "destination": "Update your profile directly in the HR portal (e.g., Workday / ADP)",
        "isCorrect": true,
        "feedback": "Correct. Self-service updates in the HR system ensure accurate payroll taxation and benefit compliance routing."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Send an email to the HR alias with your new address",
        "isCorrect": false,
        "feedback": "Violation. Forcing HR to manually transcribe your data creates administrative drag and increases the risk of payroll tax errors."
      }
    ]
  },
  {
    "id": "techstack_27",
    "artifactDescription": "The main company website and customer portal goes down, affecting all enterprise customers.",
    "sourceChannel": "System Outage",
    "options": [
      {
        "route": "System of Record",
        "destination": "Update the official public Statuspage",
        "isCorrect": true,
        "feedback": "Correct. A public Statuspage provides a single, scalable source of truth for clients, deflecting thousands of redundant support tickets."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "BCC all clients in a massive email from your Outlook",
        "isCorrect": false,
        "feedback": "Violation. Mass BCC emails look unprofessional, risk exposing client lists, and cannot be updated dynamically as the incident evolves."
      }
    ]
  },
  {
    "id": "techstack_28",
    "artifactDescription": "A prospect asks you to fill out a 200-question security compliance questionnaire before they buy.",
    "sourceChannel": "Procurement Request",
    "options": [
      {
        "route": "System of Record",
        "destination": "Process it through the official Corporate Trust and Compliance portal",
        "isCorrect": true,
        "feedback": "Correct. Trust management software uses approved data to answer repetitive security questions using vetted corporate responses."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Copy it into a Google Doc and try to guess the answers",
        "isCorrect": false,
        "feedback": "Violation. Guessing security answers creates massive legal liability. If you answer incorrectly, the company is liable for breach of contract."
      }
    ]
  },
  {
    "id": "techstack_29",
    "artifactDescription": "You draft social media copy for a highly sensitive PR announcement.",
    "sourceChannel": "PR Crisis",
    "options": [
      {
        "route": "System of Record",
        "destination": "Draft and stage for approval in the Corporate Social Management tool (e.g., Sprout Social)",
        "isCorrect": true,
        "feedback": "Correct. Enterprise social tools enforce approval workflows, preventing accidental or unauthorized publishing."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Post the drafts in a Slack channel for people to read",
        "isCorrect": false,
        "feedback": "Violation. Slack lacks publishing controls. A rogue employee could copy/paste the draft and leak it prematurely."
      }
    ]
  },
  {
    "id": "techstack_30",
    "artifactDescription": "You update the core Q4 Sales Pitch Deck with new product screenshots.",
    "sourceChannel": "Sales Enablement",
    "options": [
      {
        "route": "System of Record",
        "destination": "Publish the new version to the centralized Sales Enablement platform",
        "isCorrect": true,
        "feedback": "Correct. Sales enablement platforms guarantee that all reps globally pull the most updated, compliant version of the presentation."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Save it as 'Q4_Deck_v2.pptx' on your desktop",
        "isCorrect": false,
        "feedback": "Violation. Local storage guarantees the sales field will continue presenting outdated, legacy features to prospects."
      }
    ]
  },
  {
    "id": "techstack_31",
    "artifactDescription": "An external auditor sends you a highly sensitive report detailing a major financial compliance risk.",
    "sourceChannel": "Audit Report",
    "options": [
      {
        "route": "System of Record",
        "destination": "Route through the official secure Risk Management portal",
        "isCorrect": true,
        "feedback": "Correct. Formal vulnerability platforms triage the threat, protect confidentiality, and track the resolution timeline."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Keep the conversation going in your personal email inbox",
        "isCorrect": false,
        "feedback": "Violation. Siloing a major compliance risk in a personal inbox exposes the company to massive, untracked legal and financial liability."
      }
    ]
  },
  {
    "id": "techstack_32",
    "artifactDescription": "You purchase 50 new Adobe Creative Cloud licenses for the incoming design cohort.",
    "sourceChannel": "Software Procurement",
    "options": [
      {
        "route": "System of Record",
        "destination": "Log the licenses in the centralized IT Asset Management system",
        "isCorrect": true,
        "feedback": "Correct. Centralized IT management prevents wasted licenses, tracks software expenditure, and enables easy revocation upon offboarding."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Track who has which license in a local Excel spreadsheet",
        "isCorrect": false,
        "feedback": "Violation. Spreadsheet tracking is notoriously inaccurate, leading to thousands of dollars in wasted, unused software seats."
      }
    ]
  },
  {
    "id": "techstack_33",
    "artifactDescription": "You want to take next Friday off and need to inform the team.",
    "sourceChannel": "Time Off Request",
    "options": [
      {
        "route": "System of Record",
        "destination": "Submit a PTO request in Workday to sync with Outlook calendars",
        "isCorrect": true,
        "feedback": "Correct. Official HR requests debit your PTO balance accurately and formally block your availability across the enterprise."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Just drop a note in the team Slack channel",
        "isCorrect": false,
        "feedback": "Violation. Chat messages vanish. People will forget you are out, schedule meetings over you, and your PTO balance will be inaccurate."
      }
    ]
  },
  {
    "id": "techstack_34",
    "artifactDescription": "You record a 60-minute user research interview detailing major customer pain points.",
    "sourceChannel": "UX Research",
    "options": [
      {
        "route": "System of Record",
        "destination": "Upload the recording to the centralized User Research repository",
        "isCorrect": true,
        "feedback": "Correct. Research repositories transcribe the video and allow teams to search and tag specific user insights horizontally."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Save the MP4 video file to your local hard drive",
        "isCorrect": false,
        "feedback": "Violation. Storing user interviews locally renders the insights completely undiscoverable to the rest of the company."
      }
    ]
  },
  {
    "id": "techstack_35",
    "artifactDescription": "The team conducts a post-project review and identifies three major process bottlenecks.",
    "sourceChannel": "Team Meeting",
    "options": [
      {
        "route": "System of Record",
        "destination": "Log action items in the official project management portal (e.g., Asana / Jira)",
        "isCorrect": true,
        "feedback": "Correct. Documenting reviews centrally creates a historical ledger of team improvement and holds assignees accountable."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Just talk about it on the Zoom call and don't write it down",
        "isCorrect": false,
        "feedback": "Violation. Undocumented meetings are a waste of time. Without written action items, the exact same bottlenecks will occur on the next project."
      }
    ]
  },
  {
    "id": "techstack_36",
    "artifactDescription": "You write a highly effective email template for calming down angry customers.",
    "sourceChannel": "Customer Support",
    "options": [
      {
        "route": "System of Record",
        "destination": "Add it as a global template in the centralized customer support software (e.g., Zendesk)",
        "isCorrect": true,
        "feedback": "Correct. Centralizing templates scales your operational efficiency to the entire team, ensuring a unified brand voice."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Keep it in a Sticky Note app on your desktop and copy/paste it",
        "isCorrect": false,
        "feedback": "Violation. Hoarding successful workflows locally deprives the broader team of efficiency gains."
      }
    ]
  },
  {
    "id": "techstack_37",
    "artifactDescription": "The VP of Sales reshuffles the reporting structure for 50 account executives.",
    "sourceChannel": "Org Design",
    "options": [
      {
        "route": "System of Record",
        "destination": "Update the management hierarchy in the official HR platform (e.g., Workday)",
        "isCorrect": true,
        "feedback": "Correct. Updating the HR system automatically re-routes approval chains, permission groups, and payroll hierarchies."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Draw a new org chart in Visio and email it to the team",
        "is Court": false,
        "feedback": "Violation. A static diagram does not change backend software permissions; the company's digital infrastructure will break."
      }
    ]
  },
  {
    "id": "techstack_38",
    "artifactDescription": "You assign the new $1M quarterly quota targets to your sales representatives.",
    "sourceChannel": "Sales Management",
    "options": [
      {
        "route": "System of Record",
        "destination": "Input the targets into the official Sales Commission platform (e.g., Salesforce)",
        "isCorrect": true,
        "feedback": "Correct. Integrating quotas into the system automates commission calculations and provides reps with real-time performance dashboards."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Keep a private Excel file tracking who hit their number",
        "isCorrect": false,
        "feedback": "Violation. Shadow accounting causes severe commission disputes and deprives the finance team of accurate revenue forecasting data."
      }
    ]
  },
  {
    "id": "techstack_39",
    "artifactDescription": "You need to write the annual performance review for your direct report.",
    "sourceChannel": "Performance Management",
    "options": [
      {
        "route": "System of Record",
        "destination": "Complete the evaluation inside the official Performance Management platform",
        "isCorrect": true,
        "feedback": "Correct. Dedicated performance tools track historical growth, enforce standardized scoring, and align with salary increase cycles."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Write it in a Microsoft Word doc and email it to them",
        "isCorrect": false,
        "feedback": "Violation. Unstructured Word docs lack data normalization, making it impossible for HR to calibrate performance across the company."
      }
    ]
  },
  {
    "id": "techstack_40",
    "artifactDescription": "After a major operational crisis, the team figures out why the failure occurred.",
    "sourceChannel": "Incident Response",
    "options": [
      {
        "route": "System of Record",
        "destination": "Publish a formal incident report in the centralized knowledge base (e.g., Confluence)",
        "isCorrect": true,
        "feedback": "Correct. A centralized post-mortem ensures institutional learning, assigns preventative action items, and prevents the crisis from recurring."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Discuss it in a long email thread",
        "isCorrect": false,
        "feedback": "Violation. Email threads bury critical learnings and provide no mechanism for tracking preventative operational tasks."
      }
    ]
  },
  {
    "id": "techstack_41",
    "artifactDescription": "Customers keep requesting a new reporting feature for your software application.",
    "sourceChannel": "User Feedback",
    "options": [
      {
        "route": "System of Record",
        "destination": "Log the requests in the official Product Feedback portal",
        "isCorrect": true,
        "feedback": "Correct. Using a product feedback platform quantifies the demand, allowing management to attach a true business value to the request."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Keep a tally mark list in your physical notebook",
        "isCorrect": false,
        "feedback": "Violation. Analog tracking is inaccessible to the rest of the company. If you lose the notebook, the customer voice is erased."
      }
    ]
  },
  {
    "id": "techstack_42",
    "artifactDescription": "You need to send the monthly internal newsletter to all 500 employees.",
    "sourceChannel": "Internal Comms",
    "options": [
      {
        "route": "System of Record",
        "destination": "Send via an internal communications platform (e.g., Mailchimp)",
        "isCorrect": true,
        "feedback": "Correct. Internal comms platforms track open rates, click-throughs, and ensure formatting doesn't break across different email clients."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Send it from your personal Outlook using a massive distribution list",
        "isCorrect": false,
        "feedback": "Violation. Standard email provides zero engagement analytics, making it impossible to know if employees are actually reading company updates."
      }
    ]
  },
  {
    "id": "techstack_43",
    "artifactDescription": "Your top competitor just changed their pricing, and you need to update the sales battlecard.",
    "sourceChannel": "Competitive Intel",
    "options": [
      {
        "route": "System of Record",
        "destination": "Update the official competitive guide in the Sales Enablement portal",
        "isCorrect": true,
        "feedback": "Correct. Updating the centralized enablement asset ensures every sales rep globally is armed with the correct counter-argument."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Post a message in the #sales Slack channel",
        "isCorrect": false,
        "feedback": "Violation. The Slack message will be buried by tomorrow, leaving sales reps defenseless on client calls next week."
      }
    ]
  },
  {
    "id": "techstack_44",
    "artifactDescription": "A vendor arrives at the corporate office for an on-site meeting.",
    "sourceChannel": "Physical Security",
    "options": [
      {
        "route": "System of Record",
        "destination": "Have them sign in via the digital front desk kiosk (e.g., Envoy)",
        "isCorrect": true,
        "feedback": "Correct. Digital visitor management cross-references watchlists, signs mandatory NDAs, and maintains a digital evacuation manifest."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Have them sign their name in a paper binder at the front desk",
        "isCorrect": false,
        "feedback": "Violation. Paper logs violate data privacy (visitors can see who else signed in) and are useless during a fire evacuation."
      }
    ]
  },
  {
    "id": "techstack_45",
    "artifactDescription": "You are tasked with writing 100 standardized compliance checks for a new operational process.",
    "sourceChannel": "Process Testing",
    "options": [
      {
        "route": "System of Record",
        "destination": "Input the checks into the official Quality Assurance tracking system",
        "isCorrect": true,
        "feedback": "Correct. Dedicated tracking tools integrate with project management portals, enabling automated reporting and traceability."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Write the compliance checks in a Google Sheet",
        "isCorrect": false,
        "feedback": "Violation. Spreadsheets lack execution history, cannot integrate with automated workflows, and scale incredibly poorly."
      }
    ]
  },
  {
    "id": "techstack_46",
    "artifactDescription": "You need to request a new software application for your team to use.",
    "sourceChannel": "Software Request",
    "options": [
      {
        "route": "System of Record",
        "destination": "Submit a formal procurement and security review ticket via the IT portal",
        "isCorrect": true,
        "feedback": "Correct. Formal IT requests ensure software passes security, legal, and budget compliance checks before being introduced to the business."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Use a corporate credit card to buy it directly online and expense it",
        "isCorrect": false,
        "feedback": "Violation. Circumventing IT procurement creates 'shadow IT', leading to unvetted security risks and wasted software budgets."
      }
    ]
  },
  {
    "id": "techstack_47",
    "artifactDescription": "A sales rep claims their monthly commission check was miscalculated by $500.",
    "sourceChannel": "Payroll Dispute",
    "options": [
      {
        "route": "System of Record",
        "destination": "Submit a formal inquiry via the official Finance Helpdesk portal",
        "isCorrect": true,
        "feedback": "Correct. Ticketing financial disputes ensures the issue is tracked against expected response times and provides an auditable trail for HR."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Send an angry Slack DM to the Payroll Manager",
        "isCorrect": false,
        "feedback": "Violation. Direct messaging payroll staff bypasses queue prioritization, creates extreme stress, and leaves no official record of the financial dispute."
      }
    ]
  },
  {
    "id": "techstack_48",
    "artifactDescription": "A European user submits a legal 'Right to be Forgotten' request under data privacy laws.",
    "sourceChannel": "Data Privacy",
    "options": [
      {
        "route": "System of Record",
        "destination": "Process the deletion workflow through the official Data Privacy compliance platform",
        "isCorrect": true,
        "feedback": "Correct. Privacy management platforms orchestrate verifiable deletion across all systems to ensure absolute legal compliance."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Forward the email to a database admin and say 'can you delete this guy?'",
        "isCorrect": false,
        "feedback": "Violation. Manual deletion requests lack a verifiable audit trail, leaving the company exposed to massive regulatory non-compliance fines."
      }
    ]
  },
  {
    "id": "techstack_49",
    "artifactDescription": "You finalize the highly confidential slide deck for the upcoming Board of Directors meeting.",
    "sourceChannel": "Executive Strategy",
    "options": [
      {
        "route": "System of Record",
        "destination": "Save it to the Board Portal / secure corporate drive with restricted access",
        "isCorrect": true,
        "feedback": "Correct. Secure portals enforce strict access controls, preventing leaks of highly sensitive corporate information."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Save it to a personal USB thumb drive so you can work on it at home",
        "isCorrect": false,
        "feedback": "Violation. Transporting confidential board data on unencrypted physical media is a catastrophic security breach waiting to happen."
      }
    ]
  },
  {
    "id": "techstack_50",
    "artifactDescription": "You sign a 12-month contract for a new marketing tool.",
    "sourceChannel": "Procurement",
    "options": [
      {
        "route": "System of Record",
        "destination": "Log the contract and renewal date in the centralized IT Software Management portal",
        "isCorrect": true,
        "feedback": "Correct. Centralized software management alerts procurement 90 days before renewal, allowing time to negotiate or cancel."
      },
      {
        "route": "Ephemeral / Shadow IT",
        "destination": "Set a reminder on your personal Outlook calendar",
        "isCorrect": false,
        "feedback": "Violation. Personal calendar tracking fails if you leave the company, resulting in auto-renewals for unused software."
      }
    ]
  }
];